import { useState, type ReactNode } from "react";
import clsx from "clsx";
import type { GemStack, ItemStats, PlayerItem } from "../types";
import { getItemIconPath, RARITY_COLOR, CLASS_LABEL } from "../utils/assets";
import { formatExactNumber } from "../utils/formatNumber";
import {
  initialEnhanceLuckLevel,
  resolveAvailableSessionLuckLevel,
  setSessionEnhanceLuckLevel,
} from "../utils/sessionEnhanceLuck";
import { GemPickerModal, formatGemLabel, gemTypeColor } from "./GemPickerModal";
import { AdminAssetPath } from "./AdminAssetPath";
import { ModalBackdrop } from "./ModalBackdrop";

interface ItemDetailModalProps {
  item: PlayerItem | null;
  slotLabel?: string;
  heroClass?: string;
  gems?: GemStack[];
  socketUnlockCost?: number;
  diamonds?: number;
  onClose: () => void;
  onEquip?: () => void;
  onUnequip?: () => void;
  onSell?: () => void;
  onUpgrade?: (luckLevel: number) => void;
  luckBonusPerLevel?: number;
  onUnlockSocket?: () => void;
  onSocketGem?: (socketIndex: number, gemType: string, gemLevel: number) => void;
  onUnsocketGem?: (socketIndex: number) => void;
  footerContent?: ReactNode;
  actionLoading?: boolean;
}

export function ItemDetailModal({
  item,
  slotLabel,
  heroClass,
  gems = [],
  socketUnlockCost = 100,
  diamonds = 0,
  onClose,
  onEquip,
  onUnequip,
  onSell,
  onUpgrade,
  luckBonusPerLevel = 0.1,
  onUnlockSocket,
  onSocketGem,
  onUnsocketGem,
  footerContent,
  actionLoading,
}: ItemDetailModalProps) {
  const [pickerIndex, setPickerIndex] = useState<number | null>(null);
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);

  const t = item?.template;
  const luckStacks = gems.filter((g) => g.gem_type === "LUCK" && g.quantity > 0);
  const baseSuccessRate = item?.upgrade_success_rate ?? 100;
  const needsLuckStone = baseSuccessRate < 100;
  const sessionLuckLevel = resolveAvailableSessionLuckLevel(luckStacks);
  const upgradeSuccessRate =
    sessionLuckLevel != null && sessionLuckLevel > 0
      ? Math.min(100, baseSuccessRate + sessionLuckLevel * luckBonusPerLevel * 100)
      : baseSuccessRate;

  const handleUpgradeClick = () => {
    if (!onUpgrade) return;
    if (!needsLuckStone) {
      onUpgrade(0);
      return;
    }
    if (sessionLuckLevel != null) {
      onUpgrade(sessionLuckLevel);
      return;
    }
    setShowEnhanceModal(true);
  };
  const rarityClass = t ? (RARITY_COLOR[t.rarity] ?? RARITY_COLOR.JUNK) : "border-gray-700";
  const iconSrc = t ? getItemIconPath(t.hero_class, t.slot, t.icon, t.item_level) : null;
  const socketGems = item?.socket_gems ?? [];
  const canUnlockMore = item && t ? item.sockets_unlocked < t.max_sockets : false;
  const atMaxEnhance = item ? item.enhance_level >= item.max_enhance : false;
  const showEnhanceSection = Boolean(onUpgrade) || atMaxEnhance;

  return (
    <>
      <ModalBackdrop onClose={onClose}>
        <div className="w-full max-w-sm max-h-[85vh] overflow-y-auto bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3">
          {item && t ? (
            <>
              <div className="flex items-start gap-3">
                <div className="shrink-0 space-y-1">
                  <img
                    src={iconSrc!}
                    alt={t.name}
                    className={clsx("w-16 h-16 rounded-lg border-2 object-contain bg-kingdom-dark", rarityClass)}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <AdminAssetPath path={iconSrc!} className="max-w-[4.5rem]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white truncate">{t.name}</div>
                  <div className={clsx("text-xs font-medium", rarityClass.split(" ")[0])}>
                    {t.rarity} · Lv.{t.item_level}
                    {item.enhance_level > 0 && ` +${item.enhance_level}`}
                  </div>
                  {t.hero_class && (
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      Class: {CLASS_LABEL[t.hero_class] ?? t.hero_class}
                    </div>
                  )}
                  {t.description && <p className="text-xs text-gray-400 mt-1">{t.description}</p>}
                </div>
                <button onClick={onClose} className="text-gray-400 text-xl">
                  ✕
                </button>
              </div>

              <div className="space-y-1.5">
                <div className="text-xs text-gray-400 uppercase tracking-wide">Stats</div>
                <ItemStatRows base={item.stats_base ?? item.stats} enhance={item.stats_enhance ?? {}} />
              </div>

              {item.set_info && (
                <div className="space-y-1.5">
                  <div className="text-xs text-gray-400 uppercase tracking-wide">
                    Set bonus · Lv.{item.set_info.set_level}
                    <span className="normal-case text-gray-500 ml-1">
                      ({item.set_info.pieces_equipped}/4 equipped)
                    </span>
                  </div>
                  <SetBonusLine
                    active={item.set_info.two_piece_active}
                    text={`2 pieces: +${formatExactNumber(item.set_info.two_piece_hp)} HP`}
                  />
                  <SetBonusLine
                    active={item.set_info.four_piece_active}
                    text={`4 pieces: +${item.set_info.four_piece_mana_per_action} MP per action`}
                  />
                </div>
              )}

              <div className="bg-kingdom-dark/80 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-400">Gem sockets</div>
                  {canUnlockMore && onUnlockSocket && (
                    <button
                      type="button"
                      disabled={actionLoading || diamonds < socketUnlockCost}
                      onClick={onUnlockSocket}
                      className="text-[10px] text-kingdom-glow font-bold disabled:opacity-40"
                    >
                      Drill (💎{socketUnlockCost})
                    </button>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {Array.from({ length: t.max_sockets }).map((_, i) => {
                    const locked = i >= item.sockets_unlocked;
                    const gem = socketGems[i] ?? null;
                    return (
                      <button
                        key={i}
                        type="button"
                        disabled={locked || actionLoading}
                        onClick={() => {
                          if (locked) return;
                          if (gem && onUnsocketGem) onUnsocketGem(i);
                          else if (onSocketGem) setPickerIndex(i);
                        }}
                        className={clsx(
                          "w-12 h-12 rounded-lg border-2 flex flex-col items-center justify-center text-[9px] font-bold transition-colors",
                          locked
                            ? "border-gray-700 bg-gray-900/50 opacity-50 cursor-not-allowed"
                            : gem
                              ? "border-amber-500/60 bg-amber-950/30 hover:border-rose-400"
                              : "border-kingdom-glow/50 bg-kingdom-mid/40 hover:border-kingdom-glow",
                        )}
                        title={gem ? "Tap to remove gem" : "Tap to insert gem"}
                      >
                        {locked ? (
                          "🔒"
                        ) : gem ? (
                          <>
                            <span className={gemTypeColor(gem.type, heroClass)}>
                              {formatGemLabel(gem.type, heroClass)}
                            </span>
                            <span className="text-gray-400">Lv.{gem.level}</span>
                          </>
                        ) : (
                          <span className="text-gray-500">+</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                {!item.is_equipped && onEquip && (
                  <>
                    <button
                      onClick={onEquip}
                      disabled={actionLoading || item.can_equip === false}
                      className="flex-1 min-w-[calc(50%-4px)] py-2.5 rounded-xl bg-kingdom-glow text-black font-bold text-sm disabled:opacity-50"
                    >
                      Equip
                    </button>
                    {item.can_equip === false && (
                      <p className="w-full text-xs text-amber-300/90 text-center">
                        Requires Lv.{t.item_level}
                      </p>
                    )}
                  </>
                )}
                {item.is_equipped && onUnequip && (
                  <button
                    onClick={onUnequip}
                    disabled={actionLoading}
                    className="flex-1 min-w-[calc(50%-4px)] py-2.5 rounded-xl bg-kingdom-mid border border-kingdom-light text-kingdom-glow font-bold text-sm disabled:opacity-50"
                  >
                    Unequip
                  </button>
                )}
                {showEnhanceSection && (
                  <div className="flex-1 min-w-[calc(50%-4px)] flex flex-col gap-1">
                    {atMaxEnhance ? (
                      <button
                        type="button"
                        disabled
                        className="w-full py-2.5 rounded-xl border border-gray-600 text-gray-500 font-bold text-sm cursor-not-allowed opacity-70"
                      >
                        Max upgrade (+{item.enhance_level})
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleUpgradeClick}
                          disabled={actionLoading || !onUpgrade}
                          className="w-full py-2.5 rounded-xl border border-gold-500 text-gold-300 font-bold text-sm disabled:opacity-50"
                        >
                          Upgrade ({formatExactNumber(item.upgrade_gold_cost ?? 0)}g ·{" "}
                          {Math.round(upgradeSuccessRate)}%
                          {sessionLuckLevel != null && sessionLuckLevel > 0
                            ? ` · LUCK Lv.${sessionLuckLevel}`
                            : ""}
                          )
                        </button>
                        {needsLuckStone && (
                          <button
                            type="button"
                            onClick={() => setShowEnhanceModal(true)}
                            disabled={actionLoading}
                            className="text-[10px] text-gray-400 hover:text-kingdom-glow disabled:opacity-40"
                          >
                            {sessionLuckLevel != null && sessionLuckLevel > 0
                              ? `Luck stone Lv.${sessionLuckLevel} · change`
                              : sessionLuckLevel === 0
                                ? "No luck stone · change"
                                : "Choose luck stone"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
                {onSell && (
                  <button
                    onClick={onSell}
                    disabled={actionLoading}
                    className="flex-1 min-w-[calc(50%-4px)] py-2.5 rounded-xl border border-rose-500/50 text-rose-300 font-bold text-sm disabled:opacity-50"
                  >
                    Sell {formatExactNumber(item.sell_gold)}g
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-bold text-white">{slotLabel ?? "Equipment slot"}</div>
                <p className="text-sm text-gray-400 mt-1">No item equipped</p>
              </div>
              <button onClick={onClose} className="text-gray-400 text-xl">
                ✕
              </button>
            </div>
          )}

          {footerContent}
        </div>
      </ModalBackdrop>

      {pickerIndex != null && onSocketGem && (
        <GemPickerModal
          gems={gems}
          heroClass={heroClass}
          onClose={() => setPickerIndex(null)}
          onSelect={(gemType, level) => {
            onSocketGem(pickerIndex, gemType, level);
            setPickerIndex(null);
          }}
        />
      )}

      {showEnhanceModal && item && onUpgrade && (
        <EnhanceLuckModal
          itemName={item.template.name}
          currentPlus={item.enhance_level}
          goldCost={item.upgrade_gold_cost ?? 0}
          baseRate={baseSuccessRate}
          luckStacks={luckStacks}
          luckBonusPerLevel={luckBonusPerLevel}
          loading={actionLoading}
          onCancel={() => setShowEnhanceModal(false)}
          onConfirm={(luckLevel) => {
            setSessionEnhanceLuckLevel(luckLevel);
            setShowEnhanceModal(false);
            onUpgrade(luckLevel);
          }}
        />
      )}
    </>
  );
}

function ItemStatRows({ base, enhance }: { base: ItemStats; enhance: ItemStats }) {
  const rows: { label: string; key: keyof ItemStats }[] = [
    { label: "HP", key: "hp" },
    { label: "PATK", key: "patk" },
    { label: "MATK", key: "matk" },
    { label: "PDEF", key: "pdef" },
    { label: "MDEF", key: "mdef" },
    { label: "ATK", key: "atk" },
    { label: "DEF", key: "def_" },
  ];

  const visible = rows.filter(({ key }) => (base[key] ?? 0) > 0 || (enhance[key] ?? 0) > 0);
  if (visible.length === 0) {
    return <p className="text-sm text-gray-500">No stat bonuses</p>;
  }

  return (
    <div className="space-y-1">
      {visible.map(({ label, key }) => (
        <StatRow
          key={key}
          label={label}
          base={base[key] ?? 0}
          enhance={enhance[key] ?? 0}
        />
      ))}
    </div>
  );
}

function StatRow({ label, base, enhance }: { label: string; base: number; enhance: number }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-400">{label}</span>
      <span className="text-white tabular-nums">
        {formatExactNumber(base)}
        {enhance > 0 && (
          <span className="text-emerald-400"> + {formatExactNumber(enhance)}</span>
        )}
      </span>
    </div>
  );
}

function SetBonusLine({ active, text }: { active: boolean; text: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-300">
      <span className={clsx("w-4 text-center font-bold", active ? "text-emerald-400" : "text-gray-600")}>
        {active ? "✓" : ""}
      </span>
      <span className={active ? "text-gray-200" : undefined}>{text}</span>
    </div>
  );
}

function EnhanceLuckModal({
  itemName,
  currentPlus,
  goldCost,
  baseRate,
  luckStacks,
  luckBonusPerLevel,
  loading,
  onCancel,
  onConfirm,
}: {
  itemName: string;
  currentPlus: number;
  goldCost: number;
  baseRate: number;
  luckStacks: GemStack[];
  luckBonusPerLevel: number;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (luckLevel: number) => void;
}) {
  const [luckLevel, setLuckLevel] = useState(() => initialEnhanceLuckLevel(luckStacks));
  const bonus = luckLevel * luckBonusPerLevel * 100;
  const total = Math.min(100, baseRate + bonus);

  return (
    <ModalBackdrop onClose={onCancel} className="z-[60] items-end bg-black/70">
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3">
        <h3 className="font-bold text-white">Enhance {itemName}</h3>
        <p className="text-sm text-gray-400">
          +{currentPlus} → +{currentPlus + 1} · {Math.round(baseRate)}% base success · {formatExactNumber(goldCost)} gold
        </p>
        <p className="text-xs text-rose-300/90">
          On failure you lose the gold. If you use a luck stone, it is consumed too. The item level stays the same.
        </p>
        <select
          value={luckLevel}
          onChange={(e) => setLuckLevel(Number(e.target.value))}
          className="w-full bg-kingdom-dark border border-kingdom-mid rounded-lg px-3 py-2 text-sm text-white"
        >
          <option value={0}>No luck stone ({Math.round(baseRate)}%)</option>
          {luckStacks.map((l) => (
            <option key={`luck-${l.level}`} value={l.level}>
              LUCK Lv.{l.level} (+{Math.round(l.level * luckBonusPerLevel * 100)}%)
            </option>
          ))}
        </select>
        <div className="text-xs text-emerald-400">Final success rate: {Math.round(total)}%</div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-600 text-gray-300 text-sm font-bold">
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => onConfirm(luckLevel)}
            className="flex-1 py-2.5 rounded-xl bg-amber-700 text-white text-sm font-bold disabled:opacity-40"
          >
            Enhance
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
